import { groupMulti, isNumber, roundToSigDigits } from '@lifk/utils'
import { ExpressionSpecification } from 'mapbox-gl'

export const defaultOpacity = 0.6

const dynamicOpacity = (opacity: number): ExpressionSpecification => {
  let hovered = opacity + 0.2
  if (hovered > 1) {
    hovered = 1
  } else {
    hovered = roundToSigDigits(hovered, 2)
  }
  return [
    'case',
    [
      'any',
      ['boolean', ['feature-state', 'active'], false],
      ['boolean', ['feature-state', 'hovered'], false]
    ],
    hovered,
    opacity
  ]
}

const getDynamicOpacityFor = (
  opacity: number | string | undefined
): ExpressionSpecification =>
  dynamicOpacity(
    isNumber(opacity) ? (opacity as number) : defaultOpacity
  ) as ExpressionSpecification

interface IOpacityConfig {
  filter?: ExpressionSpecification
  opacity?: number
  minValue?: number
  maxValue?: number
  targetAttribute?: string
}

export const getMapboxOpacityExpression = (
  config: IOpacityConfig[],
  opacityByFilter: boolean,
  isGradient: boolean
): ExpressionSpecification => {
  const defaultDynamicOpacity = dynamicOpacity(defaultOpacity)
  const groupByOpacity = groupMulti(config, ({ opacity }) => [
    isNumber(opacity) ? `${opacity}` : 'default'
  ])

  const opacities = Object.keys(groupByOpacity)
  if (opacities.length === 0) {
    return defaultDynamicOpacity
  }

  if (opacities.length === 1) {
    if (opacities[0] === 'default') {
      return defaultDynamicOpacity
    } else {
      return getDynamicOpacityFor(groupByOpacity[opacities[0]][0].opacity)
    }
  }

  if (opacities.length > 1) {
    return getOpacityCaseStatement(config, opacityByFilter, isGradient)
  }

  // default (we should never get here)
  return defaultDynamicOpacity
}

const getOpacityCaseStatement = (
  config: IOpacityConfig[],
  opacityByFilter: boolean,
  isGradient: boolean
): ExpressionSpecification => {
  if (opacityByFilter) {
    return getCategoricalOpacity(config)
  } else if (
    config.every(
      ({ minValue, maxValue, targetAttribute }) =>
        isNumber(minValue) &&
        isNumber(maxValue) &&
        targetAttribute &&
        targetAttribute.length > 0
    )
  ) {
    if (isGradient) {
      return getGradientOpacity(config)
    } else {
      return getBinnedOpacity(config)
    }
  }

  // default
  return dynamicOpacity(defaultOpacity)
}

const getCategoricalOpacity = (config: IOpacityConfig[]) => {
  const mapboxOpacityExpression: ExpressionSpecification = ['case']
  config.forEach(({ filter, opacity }) => {
    mapboxOpacityExpression.push(filter)
    mapboxOpacityExpression.push(getDynamicOpacityFor(opacity))
  })
  mapboxOpacityExpression.push(dynamicOpacity(defaultOpacity))
  return mapboxOpacityExpression
}

const getGradientOpacity = (config: IOpacityConfig[]) => {
  const interpolateExpr: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['get', config[0].targetAttribute]
  ]
  config.forEach((colorStop) => {
    interpolateExpr.push(colorStop.maxValue)
    interpolateExpr.push(
      getDynamicOpacityFor(colorStop.opacity ?? defaultOpacity)
    )
  })
  return interpolateExpr
}

const getBinnedOpacity = (config: IOpacityConfig[]) => {
  const caseStatement: ExpressionSpecification = [
    'case',
    ['<=', ['get', config[0].targetAttribute], 0],
    0
  ]
  config.forEach((colorStop, index) => {
    // don't add filter for last item (use last color as case statement default)
    if (colorStop.maxValue && index + 1 < config.length) {
      caseStatement.push([
        '<',
        ['get', colorStop.targetAttribute],
        colorStop.maxValue
      ])
    }
    caseStatement.push(
      getDynamicOpacityFor(colorStop.opacity ?? defaultOpacity)
    )
  })
  return caseStatement
}
